import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { DQOIReferenceDataQuery } from '@app/cp/dqoi/state/dqoi-reference-data.query';
import { DQOIReferenceData } from '@app/cp/shared/models/DQOIReferenceData';
import { BaseGridComponent } from '@app/cp/shared/base-components/base-grid.component';
import { ConstructionService } from '@app/cp/shared/services/construction.service';
import { ConstructionSearchParam } from '@app/cp/shared/models/ConstructionSearchParam';
import { CommonFunctions } from '@app/cp/shared/static/common-functions';
import { GridDataResult, DataStateChangeEvent, SelectionEvent } from '@progress/kendo-angular-grid';
import { GItem } from '@app/cp/shared/models/GItem';
import { process, aggregateBy, CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import * as _ from 'lodash';
import { MSCLFilterClass } from '../../shared/classes/filters/MSCLFilterClass';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { NotificationSettings } from '@progress/kendo-angular-notification';
import { BaseMessageInfo } from '@app/cp/shared/models/BaseMessageInfo';
import { WipReportData } from '@app/cp/shared/models/WipReportData';
import { ContractWipReportData } from '@app/cp/shared/models/ContractWipReportData';
import { DispatchCostAdjustmentsComponent } from '../jobs/dispatch-cost-adjustments/dispatch-cost-adjustments.component';
import { GridComponent } from '@progress/kendo-angular-grid';
import { DispatchContractAdjustmentsComponent } from '../jobs/dispatch-contract-adjustments/dispatch-contract-adjustments.component';
import { DispatchCurrentCostAdjustmentsComponent } from '../jobs/dispatch-current-cost-adjustments/dispatch-current-cost-adjustments.component';
import { DispatchBtdAdjustmentsComponent } from '../jobs/dispatch-btd-adjustments/dispatch-btd-adjustments.component';
import { DispatchSixzerosixAdjustmentsComponent } from '../jobs/dispatch-sixzerosix-adjustments/dispatch-sixzerosix-adjustments.component';
import { CnstContWIPCommitSaveParam } from '@app/cp/shared/models/CnstContWIPCommitSaveParam';
import { saveAs } from "@progress/kendo-file-saver";
import { Workbook } from "@progress/kendo-angular-excel-export";
import { DispatchMultiAdjustmentsComponent } from '../jobs/dispatch-multi-adjustments/dispatch-multi-adjustments.component';
import { FeatureFlagService } from "@app/cp/shared/services/feature-flag.service";
import { CmProAuthService } from '@app/core/services';
import { UsersBus } from '@app/cp/shared/models/UsersBus.model';
import { AppClaims } from '@app/cp/shared/models/AppClaims';

interface ColumnSetting {
    field: string;
    title: string;
    format?: string;
    type: 'text' | 'numeric' | 'boolean' | 'date';
    hidden: boolean;

}

@Component({
    selector: 'cp-contract-wip-board',
    templateUrl: './contract-wip-board.component.html',
    styleUrls: ['./contract-wip-board.component.scss']
})
export class ContractWipBoardComponent extends BaseGridComponent<ContractWipReportData, null> implements OnInit, OnDestroy {

    public aggregates: any[] = [];
    public aggregateResults;
    public gridView: GridDataResult;
    public jobGridView: GridDataResult;
    public pageSize = 50;
    public confirmBtnStyle: string = 'outline';
    public confirmBtnDisabled: boolean = false;
    public confirmBtnLoading: boolean = false;
    customerNameCF: MSCLFilterClass;
    contractAdministratorCF: MSCLFilterClass;
    locationNameCF: MSCLFilterClass;
    pmCF: MSCLFilterClass;
    divisionNameCF: MSCLFilterClass;
    subs_dataRefUpdated: Subscription;

    constructor(globals: GlobalsService, featureFlagService: FeatureFlagService, private qry_rd: DQOIReferenceDataQuery, svc_data: ConstructionService, private authService: CmProAuthService) {

        super(globals, featureFlagService);
        this.allData = this.allData.bind(this);
        this.svc_data = svc_data;
        this.skip = 0;

        this.subs_dataRef = this.qry_rd.ensureRefDataIsLoaded(this.globals.companyId).subscribe();
        this.subs_dataRefUpdated = this.qry_rd.referenceDataUpdated.subscribe((data: DQOIReferenceData) => {
            this.referenceData = data
        });

        this.aggregates = [
            { field: 'contractNumber', aggregate: 'count' },
            { field: 'jobNumber', aggregate: 'count' },
            { field: 'percComplete', aggregate: 'sum' },
            { field: 'contractAmount', aggregate: 'sum' },
            { field: 'totalEstimatedContractCosts', aggregate: 'sum' },
            { field: 'estimatedContractMaterialCosts', aggregate: 'sum' },
            { field: 'estimatedContractLaborCosts', aggregate: 'sum' },
            { field: 'estimatedContractOtherCosts', aggregate: 'sum' },
            { field: 'estimatedContractPendingCRCosts', aggregate: 'sum' },
            { field: 'contractAdjustments', aggregate: 'sum' },
            { field: 'pMEstimateAdjustments', aggregate: 'sum' },
            { field: 'currentCostAdjustments', aggregate: 'sum' },
            { field: 'btdAdjustments', aggregate: 'sum' },
            { field: 'projectedGrossProfit', aggregate: 'sum' },
            { field: 'totGrossProfitPerc', aggregate: 'sum' },
            { field: 'committedCosts', aggregate: 'sum' },
            { field: 'jobsWCommittedCostsGTEstimatedCostsLTD', aggregate: 'sum' },
            { field: 'bidHrs', aggregate: 'sum' },
            { field: 'actHrs', aggregate: 'sum' },
            { field: 'percLaborhoursTD', aggregate: 'sum' },
            { field: 'contractRevenue', aggregate: 'sum' },
            { field: 'totalCurrentCosts', aggregate: 'sum' },
            { field: 'totalCurrentMaterialCosts', aggregate: 'sum' },
            { field: 'totalCurrentLaborCosts', aggregate: 'sum' },
            { field: 'totalCurrentOtherCosts', aggregate: 'sum' },
            { field: 'currentGrossProfit', aggregate: 'sum' },
            { field: 'billedToDate', aggregate: 'sum' },
            { field: 'billingsInExcessCostsEstEarningsCostEstEarningsExcessBillings', aggregate: 'sum' },
            { field: 'backlog', aggregate: 'sum' },
            { field: 'totalCostsToComplete', aggregate: 'sum' },
            { field: 'totalMaterialCostsToComplete', aggregate: 'sum' },
            { field: 'totalLaborCostsToComplete', aggregate: 'sum' },
            { field: 'totalOtherCostsToComplete', aggregate: 'sum' },
            { field: 'totalAdjustmentsToBeComplete', aggregate: 'sum' },
            { field: 'grossProfit', aggregate: 'sum' },
            { field: 'amountNotYetBilled', aggregate: 'sum' }
        ];
    }

    public newButtonData: Array<any> = [{
        text: 'Contract Adj',
        click: (jobId, jobNbr) => {

        }
    },
    {
        text: 'Estimated Cost Adj',
        click: (jobId, jobNbr) => {

        }
    },
    {
        text: 'Curr Cost Adj',
        click: (jobId, jobNbr) => {

        }
    },
    {
        text: 'BTD Adj',
        click: (jobId, jobNbr) => {

        }
    },
    {
        text: '606 Adj',
        click: (jobId, jobNbr) => {

        }
    }
    ];

    showAdjustPopup: boolean = false;
    currentContractId: any;
    currentJobIds: string[];

    subs_dataRef: Subscription;
    svc_data: ConstructionService;
    referenceData: DQOIReferenceData;
    contractAdministratorId: any;

    gridDataPrime: any[] = [];
    jobGridDataPrime: any[] = [];
    constractNo: string;
    billingMonthYear: any;
    projectManagerId: any;

    //Filters
    jobPMs: GItem[] = [];
    contractStatuses: GItem[] = [];
    customers: GItem[] = [];
    jobClasses: GItem[] = [];

    JobPMIds: GItem[] = [];
    dispatchStatusIds: GItem[] = [];
    customerIds: GItem[] = [];
    jobClassIds: GItem[] = [];

    filteredData: any[];
    exportFilteredData: any[];

    ColumnHeaders: ColumnSetting[];
    loading: boolean = true;
    commitsLoading: boolean = true;
    private initSubscription: Subscription;
    private getDataSubscription: Subscription;
    private getCommitsSubscription: Subscription;
    rights: AppClaims = new AppClaims();

    @ViewChild('jobGrid') protected grid: GridComponent;
    @ViewChild('ContCostAdj') public contAdjComp: DispatchContractAdjustmentsComponent;
    @ViewChild('EstCostAdj') public costAdjComp: DispatchCostAdjustmentsComponent;
    @ViewChild('CurrCostAdj') public currCostAdjComp: DispatchCurrentCostAdjustmentsComponent;
    @ViewChild('BTDCostAdj') public BTDAdjComp: DispatchBtdAdjustmentsComponent;
    @ViewChild('SixZeroSixCostAdj') public SixZeroSixAdjComp: DispatchSixzerosixAdjustmentsComponent;
    @ViewChild('MultiCostAdj') public MultiCostAdjComp: DispatchMultiAdjustmentsComponent;

    ngOnInit() {
        this.loading = true;
        this.commitsLoading = true;
        this.pageSize = 45;
        this.authService.subscribeToLoginEvents((user: UsersBus) => {
            if (user) {
                this.rights = user.appClaims;
                this.getCommits();
                this.getData();
            }
        });
    }
    override ngOnDestroy() {
        super.ngOnDestroy();
        this.unsubscribeAll();
        CommonFunctions.unsubscribe(this.subs_dataRef);
        CommonFunctions.unsubscribe(this.subs_dataRefUpdated);
        this.authService.unsubscribeToLoginEvents();
    }

    public gridData2: any;// = process(this.gridData, this.state);

    adjustmentSaveMode: number = 1;
    //job adj
    contCurrentJobId: any;
    contCurrentJobNumber: any;
    estAdjCurrentJobId: any;
    estAdjCurrentJobNumber: any;
    currCostCurrentJobId: any;
    currCostCurrentJobNumber: any;
    BTDCurrentJobId: any;
    BTDCurrentJobNumber: any;
    SixZeroSixCurrentJobId: any;
    SixZeroSixCurrentJobNumber: any;
    //contract adj
    multiAdjustment: boolean = false;
    contCurrentContractId: any;
    contCurrentContractNumber: any;
    estAdjCurrentContractId: any;
    estAdjCurrentContractNumber: any;
    currCostCurrentContractId: any;
    currCostCurrentContractNumber: any;
    BTDCurrentContractId: any;
    BTDCurrentContractNumber: any;
    SixZeroSixCurrentContractId: any;
    SixZeroSixCurrentContractNumber: any;
    contCurrentJobIds: any[] = [];
    estAdjCurrentJobIds: any[] = [];
    currCostCurrentJobIds: any[] = [];
    BTDCurrentJobIds: any[] = [];
    SixZeroSixCurrentJobIds: any[] = [];
    contCurrentContractSubJobIds: any[] = [];
    contCurrentContractSubJobNbrs: any[] = [];

    openAdjustmentPopup(jobId, jobNum, e) {
        console.log('JobId: ' + jobId + ', JobNumber: ' + jobNum);
        switch (e.text) {
            case "Contract Adj":
                this.adjustmentSaveMode = 1;
                this.contCurrentJobId = jobId;
                this.contCurrentJobNumber = jobNum;
                this.showAdjustPopup = true;
                break;
            case "Estimated Cost Adj":
                this.adjustmentSaveMode = 1;
                this.estAdjCurrentJobId = jobId;
                this.estAdjCurrentJobNumber = jobNum;
                this.showAdjustPopup = true;
                break;
            case "Curr Cost Adj":
                this.adjustmentSaveMode = 1;
                this.currCostCurrentJobId = jobId;
                this.currCostCurrentJobNumber = jobNum;
                this.showAdjustPopup = true;
                break;
            case "BTD Adj":
                this.adjustmentSaveMode = 1;
                this.BTDCurrentJobId = jobId;
                this.BTDCurrentJobNumber = jobNum;
                this.showAdjustPopup = true;
                break;
            case "606 Adj":
                this.adjustmentSaveMode = 1;
                this.SixZeroSixCurrentJobId = jobId;
                console.log(this.SixZeroSixCurrentJobId);
                this.SixZeroSixCurrentJobNumber = jobNum;
                this.showAdjustPopup = true;
                break;
            default:
                this.adjustmentSaveMode = 1;
                this.estAdjCurrentJobId = jobId;
                this.estAdjCurrentJobNumber = jobNum;
                this.showAdjustPopup = true;
        }
    }
    openContractAdjustmentPopup(contractId, contractNum, subJobs, e) {
        this.adjustmentSaveMode = 2;
        this.multiAdjustment = true;
        this.contCurrentContractSubJobIds = subJobs.map(item => item.jobId);
        this.contCurrentContractSubJobNbrs = subJobs.map(item => item.jobNbr);
        this.contCurrentContractId = contractId;
        this.contCurrentContractNumber = contractNum;
    }
    openMultiAdjustmentPopup(contractId, contractNum, e) {
        switch (e.text) {
            case "Contract Adj":
                this.adjustmentSaveMode = 2;
                this.multiAdjustment = true;
                this.contCurrentContractId = contractId;
                this.contCurrentContractNumber = contractNum;
                this.showAdjustPopup = true;
                break;
            case "Estimated Cost Adj":
                this.adjustmentSaveMode = 2;
                this.multiAdjustment = true;
                this.estAdjCurrentContractId = contractId;
                this.estAdjCurrentContractNumber = contractNum;
                this.showAdjustPopup = true;
                break;
            case "Curr Cost Adj":
                this.adjustmentSaveMode = 2;
                this.multiAdjustment = true;
                this.currCostCurrentContractId = contractId;
                this.currCostCurrentContractNumber = contractNum;
                this.showAdjustPopup = true;
                break;
            case "BTD Adj":
                this.adjustmentSaveMode = 2;
                this.multiAdjustment = true;
                this.BTDCurrentContractId = contractId;
                this.BTDCurrentContractNumber = contractNum;
                this.showAdjustPopup = true;
                break;
            case "606 Adj":
                this.adjustmentSaveMode = 2;
                this.multiAdjustment = true;
                this.SixZeroSixCurrentContractId = contractId;
                this.SixZeroSixCurrentContractNumber = contractNum;
                this.showAdjustPopup = true;
                break;
            default:
                this.adjustmentSaveMode = 2;
                this.multiAdjustment = true;
                this.estAdjCurrentContractId = contractId;
                this.estAdjCurrentContractNumber = contractNum;
                this.showAdjustPopup = true;
        }
    }
    closeAdjustmentPopup() {
        this.adjustmentSaveMode = 1;
        this.contCurrentJobId = null;
        this.contCurrentJobNumber = null;
        this.estAdjCurrentJobId = null;
        this.estAdjCurrentJobNumber = null;
        this.currCostCurrentJobId = null;
        this.currCostCurrentJobNumber = null;
        this.BTDCurrentJobId = null;
        this.BTDCurrentJobNumber = null;
        this.SixZeroSixCurrentJobId = null;
        this.SixZeroSixCurrentJobNumber = null;
        this.showAdjustPopup = false;
        this.multiAdjustment = false;
        this.contCurrentContractId = null;
        this.contCurrentContractNumber = null;
        this.estAdjCurrentContractId = null;
        this.estAdjCurrentContractNumber = null;
        this.currCostCurrentContractId = null;
        this.currCostCurrentContractNumber = null;
        this.BTDCurrentContractId = null;
        this.BTDCurrentContractNumber = null;
        this.SixZeroSixCurrentContractId = null;
        this.SixZeroSixCurrentContractNumber = null;
        this.contCurrentJobIds = [];
        this.estAdjCurrentJobIds = [];
        this.currCostCurrentJobIds = [];
        this.BTDCurrentJobIds = [];
        this.SixZeroSixCurrentJobIds = [];
        this.contCurrentContractSubJobIds = [];
    }

    jobGridData: WipReportData[] = [];
    testjobGridData: any[] = [{ "directCustName": "Costco Wholesale" }];

    previousCommits: GItem[] = [{ description: "Current", value: null, category: null, canSelect: true, reference: null }];
    selectedCommit: any = null;

    getCommits() {
        let param: ConstructionSearchParam = this.getParam();
        this.previousCommits = [{ description: "Current", value: null, category: null, canSelect: true, reference: null }];
        this.svc_data.getContractWIPCommits(param).subscribe((data: BaseMessageInfo) => {
            if (data.primaryReturn.length > 0) {
                data.primaryReturn.forEach((commit) => this.previousCommits.push(commit));
                this.commitsLoading = false;
            } else {
                this.handleNetworkError(JSON.stringify(data.messages));
            }

        }, error => this.handleNetworkError(error));
    }
    commitSelected(e) {
        this.selectedCommit = e;
        this.refreshData();
    }

    getData() {
        this.loading = true;
        let param: ConstructionSearchParam = this.getParam();
        this.svc_data.getContractWipReportInfo(param).subscribe((data: BaseMessageInfo) => {
            if (data.primaryReturn && data.primaryReturn.length > 0) {
                let arr = [];
                for (let i = 0; i < data.primaryReturn.length; i++) {
                    let newObj: WipReportData = new WipReportData(data.primaryReturn[i]);
                    arr.push(newObj);
                }
                let tempJobData = _.cloneDeep(arr);
                let contractArr = this.contractPivot(tempJobData);
                this.jobGridData = _.cloneDeep(arr);
                this.gridData = _.cloneDeep(contractArr);
                this.filteredData = _.cloneDeep(this.gridData);
                this.processGridData();
                this.loadEntries();

                this.loading = false;
            } else {
                this.handleNetworkError(JSON.stringify(data.messages));
            }

        },
            error => this.handleNetworkError(error));
    }

    contractPivot(jobData: WipReportData[]) {
        let contracts = jobData.map(item => item.contractId).filter((value, index, self) => {
            return self.indexOf(value) === index;
        });
        let contractArr: ContractWipReportData[] = [];
        for (let i = 0; i < jobData.length; i++) {
            if (contractArr.map(item => item.contractId).indexOf(jobData[i].contractId) == -1) {
                //not yet in contractArr
                let newContract: ContractWipReportData = new ContractWipReportData(jobData[i]);
                newContract.subjobs.push(jobData[i]);
                contractArr.push(newContract);
            } else {
                //Add to existing Contract entry
                let contractIndex = contractArr.map(item => item.contractId).indexOf(jobData[i].contractId);

                contractArr[contractIndex].contractAmount += jobData[i].contractAmount;
                contractArr[contractIndex].estimatedContractMaterialCosts += jobData[i].estimatedContractMaterialCosts;
                contractArr[contractIndex].estimatedContractLaborCosts += jobData[i].estimatedContractLaborCosts;
                contractArr[contractIndex].estimatedContractOtherCosts += jobData[i].estimatedContractOtherCosts;
                contractArr[contractIndex].estimatedContractPendingCRCosts += jobData[i].estimatedContractPendingCRCosts;
                contractArr[contractIndex].contractAdjustments += jobData[i].contractAdjustments;
                contractArr[contractIndex].pmEstimateAdjustments += jobData[i].pmEstimateAdjustments;
                contractArr[contractIndex].currentCostAdjustments += jobData[i].currentCostAdjustments;
                contractArr[contractIndex].btdAdjustments += jobData[i].btdAdjustments;
                contractArr[contractIndex].lastSixZeroSixAdjustment = jobData[i].lastSixZeroSixAdjustment ? jobData[i].lastSixZeroSixAdjustment : contractArr[contractIndex].lastSixZeroSixAdjustment;

                contractArr[contractIndex].committedCosts += jobData[i].committedCosts;
                contractArr[contractIndex].bidHrs += jobData[i].bidHrs;
                contractArr[contractIndex].actHrs += jobData[i].actHrs;
                contractArr[contractIndex].totalCurrentMaterialCosts += jobData[i].totalCurrentMaterialCosts;
                contractArr[contractIndex].totalCurrentLaborCosts += jobData[i].totalCurrentLaborCosts;
                contractArr[contractIndex].totalCurrentOtherCosts += jobData[i].totalCurrentOtherCosts;
                contractArr[contractIndex].billedToDate += jobData[i].billedToDate;
                contractArr[contractIndex].subjobs.push(jobData[i]);
            }
        }
        contractArr.forEach((contract) => {
            let totJobRevenue = contract.subjobs.map(item => item.contractRevenue).reduce((previousValue, currentValue) => previousValue + currentValue);
            contract.subjobs.forEach((job) => {
                job.scalingFactor = ((contract.percComplete && contract.contractAmount && job.contractRevenue && contract.percComplete != 0 && contract.contractAmount != 0 && job.contractRevenue != 0) ? ((contract.percComplete * contract.contractAmount) / totJobRevenue) : 1);
            });

        });
        return contractArr;
    }

    updateScaledValues() {

    }

    selectJobGridData(rowIndex: number, dataItem: ContractWipReportData) {
        let index = this.gridData.map(item => item.contractId).indexOf(dataItem.contractId);
        return this.gridData[index].subjobs;
    }

    public dataStateChange(state: DataStateChangeEvent): void {
        super.dataStateChange(state);
        this.processGridData();
    }

    public jobDataStateChange(state: DataStateChangeEvent): void {
        super.dataStateChange(state);
        this.processGridData();
    }

    processGridData() {
        this.exportFilteredData = filterBy(this.filteredData, this.state.filter);
        if (CommonFunctions.hasArrayElementValue(this.exportFilteredData)) {
            this.aggregateResults = aggregateBy(this.exportFilteredData, this.aggregates);
        }

        this.loading = false;
    }

    returnError: boolean = false;

    calculateDerivedValues() {
        this.filteredData.forEach((element, index) => {
            //T & M Profit
            //element.tAndMProfitPerc = element.tAndMProfitPerc;
            //Total Estimated Costs
            element.totEstContCosts = element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments;
            //Project Gross Profit(Loss)
            element.projGrosProfit = element.contractAmount - (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments);
            //Gross Profit %
            element.projGrosProfitPerc = (element.contractAmount - (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments)) / element.contractAmount;
            //Jobs with committed costs > estimated Costs LTD
            element.jobsCommittCostsGTEstCosts = (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments) - element.committedCosts;
            //% of Labor Hours to Date
            element.percLabHrsToDate = element.actHrs / element.bidHrs;
            //Contract Revenue
            element.contRevenue = element.contractAmount * (element.actHrs / (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments));
            //Total Current Costs
            element.totCurCosts = element.totalCurrentMaterialCosts + element.totalCurrentLaborCosts + element.totalCurrentOtherCosts;
            //Current Gross Profit(Loss)
            element.currentGrossProfit = (element.contractAmount * (element.actHrs / (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments))) - (element.totalCurrentMaterialCosts + element.totalCurrentLaborCosts + element.totalCurrentOtherCosts);
            //(Billings in Excess of Costs & Est.Earnings) / Cost & Est Earnings in Excess of Billings
            element.excessBillings = (element.contractAmount * (element.actHrs / (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments))) - (element.billedToDate);
            //Backlog
            element.backlog = element.contractAmount - (element.contractAmount * (element.actHrs / (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments)));
            //Total Costs to Complete
            element.totCostsToComplete = (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments) - (element.totalCurrentMaterialCosts + element.totalCurrentLaborCosts + element.totalCurrentOtherCosts);
            //Total Material Costs to Complete
            element.totMatCostsToComplete = element.estimatedContractMaterialCosts - element.totalCurrentMaterialCosts;
            //Total Labor Costs to Complete
            element.totLabCostsToComplete = element.estimatedContractLaborCosts - element.totalCurrentLaborCosts;
            //Total Other Costs to Complete
            element.totOthCostsToComplete = element.estimatedContractOtherCosts - element.totalCurrentOtherCosts;
            //Total Adjustments to Complete
            element.totAdjCostsToComplete = element.pmEstimateAdjustments;
            //Gross profit(Loss)
            element.grossProfitLoss = (element.contractAmount - (element.contractAmount * (element.actHrs / (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments)))) - ((element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments) - (element.totalCurrentMaterialCosts + element.totalCurrentLaborCosts + element.totalCurrentOtherCosts));
            //Gross Profit %
            element.grossProfPerc = ((element.contractAmount - (element.contractAmount * (element.actHrs / (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments)))) - ((element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments) - (element.totalCurrentMaterialCosts + element.totalCurrentLaborCosts + element.totalCurrentOtherCosts))) / (element.contractAmount - (element.contractAmount * (element.actHrs / (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments))));
            //Amount Not Yet Billed
            element.amtNotYetBilled = (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments) - ((element.contractAmount * (element.actHrs / (element.estimatedContractMaterialCosts + element.estimatedContractLaborCosts + element.estimatedContractOtherCosts + element.pmEstimateAdjustments))) - (element.billedToDate));
        });

    }

    handleNetworkError(error) {
        this.showError();
    }

    showError() {
        this.loading = false;
        this.commitsLoading = false;
        this.returnError = true;
    }

    hideError() {
        this.returnError = false;
    }

    clearData() {
        this.gridData = [];
        this.filteredData = [];
        this.loading = true;
        this.commitsLoading = true;
    }

    clearJobData() {
        this.jobGridData = [];
        this.filteredData = [];
        this.loading = true;
    }

    refreshData() {
        this.filteredData = [];
        this.clearData();
        this.getCommits();
        this.getData();
    }

    savingCommit: boolean = false;
    commitWipData() {
        this.savingCommit = true;
        let commitParam = new CnstContWIPCommitSaveParam();
        commitParam.insertUserId = this.globals.userId;
        commitParam.personId = this.globals.userId;
        this.svc_data.saveContractWipCommit(commitParam).subscribe((data: BaseMessageInfo) => {
            if (!data.saveError) {
                this.globals.notifySuccess('Commit Saved');
                this.savingCommit = false;
                this.refreshData();
            } else {
                this.handleCommitError(JSON.stringify(data.messages));
            }
        },
            error => this.handleCommitError(error));
        this.refreshData();

    }

    handleCommitError(error) {
        this.savingCommit = false;
        this.globals.notifyError('There was a problem saving the Commit');
    }

    unsubscribeAll() {
        if (this.getDataSubscription) {
            this.getDataSubscription.unsubscribe();
        }
        if (this.getCommitsSubscription) {
            this.getCommitsSubscription.unsubscribe();
        }
    }

    getParam(initial: boolean = false): ConstructionSearchParam {
        let param: ConstructionSearchParam = new ConstructionSearchParam();
        if (!initial) {
            param.userId = this.globals.userId;
            param.companyId = this.globals.companyId;
            param.contractAdministratorId = this.contractAdministratorId;
            param.jobClassIds = this.jobClassIds; // CommonFunctions.getArrayFromCommaDelimitedString(this.jobClassIds);
            param.dispatchStatusIds = this.dispatchStatusIds; // CommonFunctions.getArrayFromCommaDelimitedString(this.dispatchStatusIds);
            param.contractNo = this.constractNo;
            if (this.billingMonthYear != null) {
                param.billingMonthYear = this.billingMonthYear;
            } else {
                param.billingMonthYear = new Date();
                param.billingMonthYear.setDate(1);
            }
            param.contractWipCommitId = this.selectedCommit;
        }
        return param;
    }

    public openNewTab(linkObj: any) {
        window.open(linkObj.url, "_blank");
    }

    private loadEntries(): void {
        this.gridView = {
            data: this.filteredData.slice(this.skip, this.skip + this.pageSize),
            total: this.filteredData.length
        };
    }

    public expandDetail() {
        return true;
    }

    public filter: CompositeFilterDescriptor;
    public showTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;
        if ((element.nodeName === 'TD' || element.nodeName === 'TH')
            && element.offsetWidth < element.scrollWidth) {
            this.tooltipDir.toggle(element);
        } else {
            this.tooltipDir.hide();
        }
    }

    public exportGridToExcel(): void {

        this.grid.saveAsExcel();
    }
    public allData(indata: ExcelExportData): ExcelExportData {
        if (!this.exportFilteredData) this.exportFilteredData = [];
        const result: ExcelExportData = {
            data: process(this.exportFilteredData as any, {}).data,
        };
        return result;
    }

    public onExcelExport(args: any): void {
        // Prevent automatically saving the file. We will save it manually after we fetch and add the details
        args.preventDefault();

        this.loading = true;

        const mainRows = [];
        const workbook = args.workbook;
        workbook.sheets.push(_.cloneDeep(workbook.sheets[0]));
        workbook.sheets.push(_.cloneDeep(workbook.sheets[0]));

        const rows = workbook.sheets[0].rows;
        const rowsOne = workbook.sheets[1].rows;
        let rowsTwo = workbook.sheets[2].rows;
        rowsTwo.splice(0, rowsTwo.length, []);
        workbook.sheets[0].title = 'Combined';
        workbook.sheets[1].title = 'Contracts';
        workbook.sheets[2].title = 'Jobs';

        // Get the default header styles.
        // Aternatively set custom styles for the details
        // https://www.telerik.com/kendo-angular-ui/components/excelexport/api/WorkbookSheetRowCell/
        const headerOptions = rows[0].cells[0];

        const data = this.allData(null).data;

        // add the detail data to the generated master sheet rows
        // loop backwards in order to avoid changing the rows index
        for (let row = data.length - 1; row >= 0; row--) {
            const details = data[row].subjobs;

            // add the detail data
            for (let detailRow = details.length - 1; detailRow >= 0; detailRow--) {
                const job = details[detailRow];

                rows.splice(row + 2, 0, {
                    cells: [
                        {},
                        { value: job.directCustName },
                        { value: job.directCustOicId },
                        { value: job.locationName },
                        { value: job.state },
                        { value: job.endUseCustName },
                        { value: job.endUseCustOicId },
                        { value: job.accountClassName },
                        { value: job.jobNbr },
                        { value: job.dispatchStatusName },
                        { value: job.pmName },
                        { value: job.pmEmpNo },
                        { value: job.contractNbr },
                        { value: job.contDispatchStatusName },
                        { value: job.tAndM },
                        { value: job.tAndMProfitPerc },
                        { value: (new Date(job.contractDate)).toLocaleDateString('en-US') },
                        { value: (new Date(job.projectedEndDate)).toLocaleDateString('en-US') },
                        { value: (new Date(job.contractCloseDate)).toLocaleDateString('en-US') },
                        { value: job.percComplete },
                        { value: null },
                        { value: null },
                        { value: job.contractAmount },
                        { value: job.TandMContractAmount },
                        { value: job.contractAdjustments },
                        { value: job.adjContAmount },
                        { value: job.totalEstimatedContractCosts },
                        { value: job.estAdjust },
                        { value: job.adjEstAmount },
                        { value: job.estimatedContractMaterialCosts },
                        { value: job.estimatedContractLaborCosts },
                        { value: job.estimatedContractOtherCosts },
                        { value: job.estimatedContractPendingCRCosts },
                        { value: job.pmEstimateAdjustments },
                        { value: job.tandMEstimate },
                        { value: job.projectedGrossProfit },
                        { value: job.totGrossProfitPerc },
                        { value: job.committedCosts },
                        { value: job.jobsWCommittedCostsGTEstimatedCostsLTD },
                        { value: job.bidHrs },
                        { value: job.actHrs },
                        { value: job.percBidLaborHrsIncurred },
                        { value: job.percBidLaborDollarsIncurred },
                        { value: job.percLaborhoursTD },
                        { value: job.contractRevenue },
                        { value: null },
                        { value: job.sixzerosixEntry },
                        { value: job.totalCurrentCosts },
                        { value: job.currentCostAdjustments },
                        { value: job.adjCurrentCosts },
                        { value: job.totalCurrentMaterialCosts },
                        { value: job.totalCurrentLaborCosts },
                        { value: job.totalCurrentOtherCosts },
                        { value: job.currentGrossProfit },
                        { value: job.billedToDate },
                        { value: null },
                        { value: job.btdAdjustments },
                        { value: job.adjBTD },
                        { value: job.billingsInExcessCostsEstEarningsCostEstEarningsExcessBillings },
                        { value: job.backlog },
                        { value: job.totalCostsToComplete },
                        { value: job.totalMaterialCostsToComplete },
                        { value: job.totalLaborCostsToComplete },
                        { value: job.totalOtherCostsToComplete },
                        { value: job.grossProfit },
                        { value: job.grossProfitPerc },
                        { value: job.amountNotYetBilled },
                        { value: job.divisionName },
                        { value: job.divisionSageId },
                        { value: job.changeInPMContract },
                        { value: job.changeInPMEstimate },
                        { value: job.changeInPMCost },
                        { value: job.changeInPMBilled }
                    ],
                });
            }

            // add the detail header
            rows.splice(row + 2, 0, {
                cells: [
                    {},
                    Object.assign({}, headerOptions, { value: "Direct Customer" }),
                    Object.assign({}, headerOptions, { value: "Direct Customer OIC Number" }),
                    Object.assign({}, headerOptions, { value: "Location" }),
                    Object.assign({}, headerOptions, { value: "State" }),
                    Object.assign({}, headerOptions, { value: "End Use Customer" }),
                    Object.assign({}, headerOptions, { value: "End Use Customer OIC Number" }),
                    Object.assign({}, headerOptions, { value: "Class" }),
                    Object.assign({}, headerOptions, { value: "Job #" }),
                    Object.assign({}, headerOptions, { value: "Job Status" }),
                    Object.assign({}, headerOptions, { value: "Proj. Mgr." }),
                    Object.assign({}, headerOptions, { value: "Proj. Mgr. EmpNo" }),
                    Object.assign({}, headerOptions, { value: "Contract #" }),
                    Object.assign({}, headerOptions, { value: "Contract Status" }),
                    Object.assign({}, headerOptions, { value: "T & M" }),
                    Object.assign({}, headerOptions, { value: "T & M Profit %" }),
                    Object.assign({}, headerOptions, { value: "Contract Date" }),
                    Object.assign({}, headerOptions, { value: "Projected End Date" }),
                    Object.assign({}, headerOptions, { value: "Contract Close Date" }),
                    Object.assign({}, headerOptions, { value: "% Complete" }),
                    Object.assign({}, headerOptions, { value: "Scale Factor" }),
                    Object.assign({}, headerOptions, { value: "% Complete Scaled" }),
                    Object.assign({}, headerOptions, { value: "Contract Amount" }),
                    Object.assign({}, headerOptions, { value: "T&M Contract $" }),
                    Object.assign({}, headerOptions, { value: "Contract Adjust" }),
                    Object.assign({}, headerOptions, { value: "Adj Cont Amt" }),
                    Object.assign({}, headerOptions, { value: "Total Estimated Contract Costs" }),
                    Object.assign({}, headerOptions, { value: "Est Adjustment" }),
                    Object.assign({}, headerOptions, { value: "Adj Est Amt" }),
                    Object.assign({}, headerOptions, { value: "Estimated Contract Material Costs" }),
                    Object.assign({}, headerOptions, { value: "Estimated Contract Labor Costs" }),
                    Object.assign({}, headerOptions, { value: "Estimated Contract Other Costs" }),
                    Object.assign({}, headerOptions, { value: "Estimated Contract PendingCR Costs" }),
                    Object.assign({}, headerOptions, { value: "PM Estimate Adjustments" }),
                    Object.assign({}, headerOptions, { value: "T&M Estimate" }),
                    Object.assign({}, headerOptions, { value: "Projected Gross Profit(Loss)" }),
                    Object.assign({}, headerOptions, { value: "Gross Profit %" }),
                    Object.assign({}, headerOptions, { value: "Committed Costs" }),
                    Object.assign({}, headerOptions, { value: "Total Estimated Less Total Committed" }),
                    Object.assign({}, headerOptions, { value: "Quote Hrs" }),
                    Object.assign({}, headerOptions, { value: "Act Hrs" }),
                    Object.assign({}, headerOptions, { value: "% of Bid Labor hrs incurred" }),
                    Object.assign({}, headerOptions, { value: "% of Bid Labor $ incurred" }),
                    Object.assign({}, headerOptions, { value: "% of Labor hours To date" }),
                    Object.assign({}, headerOptions, { value: "Contract Revenue" }),
                    Object.assign({}, headerOptions, { value: "Contract Revenue Scaled" }),
                    Object.assign({}, headerOptions, { value: "606 entry" }),
                    Object.assign({}, headerOptions, { value: "Total Current Costs" }),
                    Object.assign({}, headerOptions, { value: "Curr Cost Adj" }),
                    Object.assign({}, headerOptions, { value: "Adjusted Current Costs" }),
                    Object.assign({}, headerOptions, { value: "Total Current Material Costs" }),
                    Object.assign({}, headerOptions, { value: "Total Current Labor Costs" }),
                    Object.assign({}, headerOptions, { value: "Total Current Other Costs" }),
                    Object.assign({}, headerOptions, { value: "Current Gross Profit (Loss)" }),
                    Object.assign({}, headerOptions, { value: "Billed to Date" }),
                    Object.assign({}, headerOptions, { value: "Billed to Date Scaled" }),
                    Object.assign({}, headerOptions, { value: "BTD Adjustment" }),
                    Object.assign({}, headerOptions, { value: "Adjusted BTD" }),
                    Object.assign({}, headerOptions, { value: "(Billings in Excess of Costs & Est. Earnings)/Cost & Est Earnings in Excess of Billings" }),
                    Object.assign({}, headerOptions, { value: "Backlog" }),
                    Object.assign({}, headerOptions, { value: "Total Costs to Complete" }),
                    Object.assign({}, headerOptions, { value: "Total Material Costs to Complete" }),
                    Object.assign({}, headerOptions, { value: "Total Labor Costs to Complete" }),
                    Object.assign({}, headerOptions, { value: "Total Other Costs to Complete" }),
                    Object.assign({}, headerOptions, { value: "Gross Profit (Loss)" }),
                    Object.assign({}, headerOptions, { value: "Gross Profit (%)" }),
                    Object.assign({}, headerOptions, { value: "Amount Not Yet Billed" }),
                    Object.assign({}, headerOptions, { value: "Division" }),
                    Object.assign({}, headerOptions, { value: "G/L Code" }),
                    Object.assign({}, headerOptions, { value: "Change in PM Contract" }),
                    Object.assign({}, headerOptions, { value: "Change in PM Estimate" }),
                    Object.assign({}, headerOptions, { value: "Change in PM Cost" }),
                    Object.assign({}, headerOptions, { value: "Change in PM Billed" })
                ],
            });
        }

        //handle Job Sheet
        // add the detail header
        rowsTwo.splice(0, 0, {
            cells: [
                Object.assign({}, headerOptions, { value: "Direct Customer ID" }),
                Object.assign({}, headerOptions, { value: "Direct Customer OIC Number" }),
                Object.assign({}, headerOptions, { value: "Direct Customer" }),
                Object.assign({}, headerOptions, { value: "Location" }),
                Object.assign({}, headerOptions, { value: "State" }),
                Object.assign({}, headerOptions, { value: "End Use Customer ID" }),
                Object.assign({}, headerOptions, { value: "End Use Customer OIC Number" }),
                Object.assign({}, headerOptions, { value: "End Use Customer" }),
                Object.assign({}, headerOptions, { value: "Job Class" }),
                Object.assign({}, headerOptions, { value: "Job #" }),
                Object.assign({}, headerOptions, { value: "Job Status" }),
                Object.assign({}, headerOptions, { value: "Job Proj. Mgr." }),
                Object.assign({}, headerOptions, { value: "Job Proj. Mgr. EmpNo" }),
                Object.assign({}, headerOptions, { value: "Job Proj. Mgr. ID" }),
                Object.assign({}, headerOptions, { value: "Contract #" }),
                Object.assign({}, headerOptions, { value: "Contract Class" }),
                Object.assign({}, headerOptions, { value: "Contract Status" }),
                Object.assign({}, headerOptions, { value: "Contract Proj. Mgr." }),
                Object.assign({}, headerOptions, { value: "Contract Proj. Mgr. EmpNo" }),
                Object.assign({}, headerOptions, { value: "Contract Proj. Mgr. ID" }),
                Object.assign({}, headerOptions, { value: "T & M" }),
                Object.assign({}, headerOptions, { value: "T & M Profit %" }),
                Object.assign({}, headerOptions, { value: "Contract Date" }),
                Object.assign({}, headerOptions, { value: "Projected End Date" }),
                Object.assign({}, headerOptions, { value: "Contract Close Date" }),
                Object.assign({}, headerOptions, { value: "% Complete" }),
                Object.assign({}, headerOptions, { value: "Scale Factor" }),
                Object.assign({}, headerOptions, { value: "% Complete Scaled" }),
                Object.assign({}, headerOptions, { value: "Contract Amount" }),
                Object.assign({}, headerOptions, { value: "T&M Contract $" }),
                Object.assign({}, headerOptions, { value: "Contract Adjust" }),
                Object.assign({}, headerOptions, { value: "Adj Cont Amt" }),
                Object.assign({}, headerOptions, { value: "Total Estimated Contract Costs" }),
                Object.assign({}, headerOptions, { value: "Est Adjustment" }),
                Object.assign({}, headerOptions, { value: "Adj Est Amt" }),
                Object.assign({}, headerOptions, { value: "Estimated Contract Material Costs" }),
                Object.assign({}, headerOptions, { value: "Estimated Contract Labor Costs" }),
                Object.assign({}, headerOptions, { value: "Estimated Contract Other Costs" }),
                Object.assign({}, headerOptions, { value: "Estimated Contract PendingCR Costs" }),
                Object.assign({}, headerOptions, { value: "PM Estimate Adjustments" }),
                Object.assign({}, headerOptions, { value: "T&M Estimate" }),
                Object.assign({}, headerOptions, { value: "Projected Gross Profit(Loss)" }),
                Object.assign({}, headerOptions, { value: "Gross Profit %" }),
                Object.assign({}, headerOptions, { value: "Committed Costs" }),
                Object.assign({}, headerOptions, { value: "Total Estimated Less Total Committed" }),
                Object.assign({}, headerOptions, { value: "Quote Hrs" }),
                Object.assign({}, headerOptions, { value: "Act Hrs" }),
                Object.assign({}, headerOptions, { value: "% of Bid Labor hrs incurred" }),
                Object.assign({}, headerOptions, { value: "% of Bid Labor $ incurred" }),
                Object.assign({}, headerOptions, { value: "% of Labor hours To date" }),
                Object.assign({}, headerOptions, { value: "Contract Revenue" }),
                Object.assign({}, headerOptions, { value: "Contract Revenue Scaled" }),
                Object.assign({}, headerOptions, { value: "606 entry" }),
                Object.assign({}, headerOptions, { value: "Total Current Costs" }),
                Object.assign({}, headerOptions, { value: "Curr Cost Adj" }),
                Object.assign({}, headerOptions, { value: "Adjusted Current Costs" }),
                Object.assign({}, headerOptions, { value: "Total Current Material Costs" }),
                Object.assign({}, headerOptions, { value: "Total Current Labor Costs" }),
                Object.assign({}, headerOptions, { value: "Total Current Other Costs" }),
                Object.assign({}, headerOptions, { value: "Current Gross Profit (Loss)" }),
                Object.assign({}, headerOptions, { value: "Billed to Date" }),
                Object.assign({}, headerOptions, { value: "Billed to Date Scaled" }),
                Object.assign({}, headerOptions, { value: "BTD Adjustment" }),
                Object.assign({}, headerOptions, { value: "Adjusted BTD" }),
                Object.assign({}, headerOptions, { value: "(Billings in Excess of Costs & Est. Earnings)/Cost & Est Earnings in Excess of Billings" }),
                Object.assign({}, headerOptions, { value: "Backlog" }),
                Object.assign({}, headerOptions, { value: "Total Costs to Complete" }),
                Object.assign({}, headerOptions, { value: "Total Material Costs to Complete" }),
                Object.assign({}, headerOptions, { value: "Total Labor Costs to Complete" }),
                Object.assign({}, headerOptions, { value: "Total Other Costs to Complete" }),
                Object.assign({}, headerOptions, { value: "Gross Profit (Loss)" }),
                Object.assign({}, headerOptions, { value: "Gross Profit (%)" }),
                Object.assign({}, headerOptions, { value: "Amount Not Yet Billed" }),
                Object.assign({}, headerOptions, { value: "Division" }),
                Object.assign({}, headerOptions, { value: "G/L Code" }),
                Object.assign({}, headerOptions, { value: "Change in PM Contract" }),
                Object.assign({}, headerOptions, { value: "Change in PM Estimate" }),
                Object.assign({}, headerOptions, { value: "Change in PM Cost" }),
                Object.assign({}, headerOptions, { value: "Change in PM Billed" })
            ],
        });
        let k = 1;
        for (let row = 0; row < data.length; row++) {

            const details = data[row].subjobs;

            // add the detail data
            for (let detailRow = 0; detailRow < details.length; detailRow++) {
                const job = details[detailRow];

                rowsTwo.splice(k, 0, {
                    cells: [
                        { value: job.directCustId },
                        { value: job.directCustOicId },
                        { value: job.directCustName },
                        { value: job.locationName },
                        { value: job.state },
                        { value: job.endUseCustId },
                        { value: job.endUseCustOicId },
                        { value: job.endUseCustName },
                        { value: job.accountClassName },
                        { value: job.jobNbr },
                        { value: job.dispatchStatusName },
                        { value: job.pmName },
                        { value: job.pmEmpNo },
                        { value: job.pmEmpID },
                        { value: job.contractNbr },
                        { value: job.contAccountClassName },
                        { value: job.contDispatchStatusName },
                        { value: job.contPMName },
                        { value: job.contPMEmpNo },
                        { value: job.contPMEmpId },
                        { value: job.tAndM },
                        { value: job.tAndMProfitPerc },
                        { value: (new Date(job.contractDate)).toLocaleDateString('en-US') },
                        { value: (new Date(job.projectedEndDate)).toLocaleDateString('en-US') },
                        { value: (new Date(job.contractCloseDate)).toLocaleDateString('en-US') },
                        { value: job.percComplete },
                        { value: null },
                        { value: null },
                        { value: job.contractAmount },
                        { value: job.TandMContractAmount },
                        { value: job.contractAdjustments },
                        { value: job.adjContAmount },
                        { value: job.totalEstimatedContractCosts },
                        { value: job.estAdjust },
                        { value: job.adjEstAmount },
                        { value: job.estimatedContractMaterialCosts },
                        { value: job.estimatedContractLaborCosts },
                        { value: job.estimatedContractOtherCosts },
                        { value: job.estimatedContractPendingCRCosts },
                        { value: job.pmEstimateAdjustments },
                        { value: job.tandMEstimate },
                        { value: job.projectedGrossProfit },
                        { value: job.totGrossProfitPerc },
                        { value: job.committedCosts },
                        { value: job.jobsWCommittedCostsGTEstimatedCostsLTD },
                        { value: job.bidHrs },
                        { value: job.actHrs },
                        { value: job.percBidLaborHrsIncurred },
                        { value: job.percBidLaborDollarsIncurred },
                        { value: job.percLaborhoursTD },
                        { value: job.contractRevenue },
                        { value: null },
                        { value: job.sixzerosixEntry },
                        { value: job.totalCurrentCosts },
                        { value: job.currentCostAdjustments },
                        { value: job.adjCurrentCosts },
                        { value: job.totalCurrentMaterialCosts },
                        { value: job.totalCurrentLaborCosts },
                        { value: job.totalCurrentOtherCosts },
                        { value: job.currentGrossProfit },
                        { value: job.billedToDate },
                        { value: null },
                        { value: job.btdAdjustments },
                        { value: job.adjBTD },
                        { value: job.billingsInExcessCostsEstEarningsCostEstEarningsExcessBillings },
                        { value: job.backlog },
                        { value: job.totalCostsToComplete },
                        { value: job.totalMaterialCostsToComplete },
                        { value: job.totalLaborCostsToComplete },
                        { value: job.totalOtherCostsToComplete },
                        { value: job.grossProfit },
                        { value: job.grossProfitPerc },
                        { value: job.amountNotYetBilled },
                        { value: job.divisionName },
                        { value: job.divisionSageId },
                        { value: job.changeInPMContract },
                        { value: job.changeInPMEstimate },
                        { value: job.changeInPMCost },
                        { value: job.changeInPMBilled }
                    ],
                });
                k++;
            }

        }

        // create a Workbook and save the generated data URL
        // https://www.telerik.com/kendo-angular-ui/components/excelexport/api/Workbook/
        new Workbook(workbook).toDataURL().then((dataUrl: string) => {
            // https://www.telerik.com/kendo-angular-ui/components/filesaver/
            saveAs(dataUrl, "WIP_CONTRACT_REPORT.xlsx");
            this.loading = false;
        });
    }
}
